import React from "react";
import UserAvatar from "../../components/UserAvatar";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaUsers,
  FaUser,
  FaIndustry,
  FaCalendarAlt,
  FaChartLine,
} from "react-icons/fa";

function TenantCards({ tenant }) {
  // Calculate days elapsed since creation
  const daysElapsed = tenant.createdAt
    ? Math.floor(
        (new Date() - tenant.createdAt.toDate()) / (1000 * 60 * 60 * 24)
      )
    : 0;

  // Determine status style
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "on hold":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer h-full flex flex-col">
      {/* Card Header */}
      <div className="bg-gradient-to-r from-[#002368] to-[#002368]/90 text-white p-4 relative">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1">{tenant.tenantName}</h3>
            <div className="flex items-center text-xs text-white/80">
              <FaIndustry className="mr-1" />{" "}
              {tenant.tenantIndustry || "Industry not specified"}
            </div>
          </div>

          {/* Status Badge */}
          <div
            className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusStyle(
              tenant.status
            )}`}
          >
            {tenant.status || "No Status"}
          </div>
        </div>

        {/* Pipeline Status */}
        {tenant.pipeline_status && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span>Pipeline Progress</span>
              <span>{tenant.pipeline_status}</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-1.5">
              <div
                className="bg-[#ff5003] h-1.5 rounded-full"
                style={{ width: tenant.pipeline_status || "0%" }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4 flex-1">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-start">
            <FaBuilding className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Size</div>
              <div className="font-medium">{tenant.requirementSize} sqm</div>
            </div>
          </div>

          <div className="flex items-start">
            <FaMapMarkerAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Location</div>
              <div className="font-medium">
                {tenant.requirementLocation || "Not specified"}
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FaUser className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Type</div>
              <div className="font-medium">
                {tenant.requirementType || "Not specified"}
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <FaCalendarAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Move-in</div>
              <div className="font-medium">
                {tenant.requirementMoveInDate
                  ? new Date(tenant.requirementMoveInDate).toLocaleDateString()
                  : "Not specified"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card Footer */}
      <div className="border-t border-gray-100 p-3 bg-gray-50 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {daysElapsed === 0 ? "Added today" : `${daysElapsed} days ago`}
        </div>

        <div className="flex -space-x-2">
          {tenant.team_members?.map(
            (member, index) =>
              index < 3 && (
                <div key={index} className="flex items-center">
                  <UserAvatar email={member} size="sm" />
                </div>
              )
          )}
          {tenant.team_members?.length > 3 && (
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-700 border border-white">
              +{tenant.team_members.length - 3}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TenantCards;
